import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useCreateManualPolicy } from '@api/hooks/useMemories';
import { useSearchQBAccounts, useSearchQBItems } from '@api/hooks/useQBItems';
import { useVendorList } from '@api/hooks/useVendors';
import { Button } from '@components/common/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@components/common/Dialog';
import { Input } from '@components/common/Input';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { InfoTooltip } from '@components/common/InfoTooltip';
import type { PolicyFamily } from '@schemas/api';

interface PolicyCreatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: () => void;
}

type PolicyScope = 'tenant' | 'vendor';

const documentTypeOptions = [
  { id: '', label: 'Any document type' },
  { id: 'invoice', label: 'Invoices' },
  { id: 'receipt', label: 'Receipts' },
  { id: 'purchase_order', label: 'Purchase orders' },
];

export const PolicyCreatorDialog = ({ open, onOpenChange, onSubmitted }: PolicyCreatorDialogProps) => {
  const [family, setFamily] = useState<PolicyFamily>('coding.gl_account');
  const [scope, setScope] = useState<PolicyScope>('tenant');
  const [vendorId, setVendorId] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [lineDescription, setLineDescription] = useState('');
  const [targetRef, setTargetRef] = useState('');
  const createPolicy = useCreateManualPolicy();
  const vendors = useVendorList(0, 200);
  const accounts = useSearchQBAccounts('', undefined, 0, 200);
  const items = useSearchQBItems('', 0, 200);

  const targetOptions = useMemo(() => {
    if (family === 'coding.gl_account') {
      return (accounts.data?.accounts ?? []).map((account) => ({
        id: account.id,
        label: account.account_number ? `${account.account_number} · ${account.name}` : account.name,
        description: account.account_type || undefined,
      }));
    }
    return (items.data?.items ?? []).map((item) => ({
      id: item.id,
      label: item.name,
      description: item.sku || item.item_type || undefined,
    }));
  }, [accounts.data?.accounts, family, items.data?.items]);

  const vendorOptions = useMemo(() => (
    (vendors.data?.items ?? []).map((vendor) => ({
      id: vendor.id,
      label: vendor.legal_name || vendor.company_name || 'Unnamed vendor',
      description: vendor.company_name && vendor.company_name !== vendor.legal_name
        ? vendor.company_name
        : vendor.email || undefined,
    }))
  ), [vendors.data?.items]);

  const isLoadingOptions = vendors.isLoading || accounts.isLoading || items.isLoading;
  const isValid = Boolean(lineDescription.trim() && targetRef && (scope === 'tenant' || vendorId));
  const targetLabel = family === 'coding.gl_account' ? 'GL account' : 'ERP item';

  const submit = async () => {
    if (!isValid) return;
    await createPolicy.mutateAsync({
      family,
      scope,
      vendor_id: scope === 'vendor' ? vendorId : undefined,
      document_type: documentType
        ? documentType as 'invoice' | 'receipt' | 'purchase_order'
        : undefined,
      line_description: lineDescription.trim(),
      target_ref: targetRef,
    });
    onOpenChange(false);
    onSubmitted();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Create a coding policy</DialogTitle>
            <InfoTooltip>Policies are reviewed before becoming active and guide future matching lines.</InfoTooltip>
          </div>
        </DialogHeader>

        {isLoadingOptions ? <LoadingSpinner /> : <div className="space-y-6">
          <fieldset>
            <legend className="text-sm font-medium text-content-secondary">What should EZ AP suggest?</legend>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {([
                ['coding.gl_account', 'GL account', 'Code matching lines to an account'],
                ['coding.erp_item', 'ERP item', 'Use an item for matching lines'],
              ] as Array<[PolicyFamily, string, string]>).map(([value, title, description]) => (
                <label key={value} className={`cursor-pointer rounded-lg border p-4 ${family === value ? 'border-primary-500 bg-primary-50' : 'border-border-default'}`}>
                  <input
                    type="radio"
                    name="policy-family"
                    value={value}
                    checked={family === value}
                    onChange={() => { setFamily(value); setTargetRef(''); }}
                    className="sr-only"
                  />
                  <span className="block font-medium text-content-primary">{title}</span>
                  <span className="mt-1 block text-xs text-content-secondary">{description}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-content-secondary">Where should it apply?</legend>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-content-primary">
              <label className="flex cursor-pointer items-center gap-2"><input type="radio" name="policy-scope" checked={scope === 'tenant'} onChange={() => setScope('tenant')} /> All vendors</label>
              <label className="flex cursor-pointer items-center gap-2"><input type="radio" name="policy-scope" checked={scope === 'vendor'} onChange={() => setScope('vendor')} /> One vendor</label>
            </div>
            {scope === 'vendor' && <div className="mt-3">
              <SearchableSelect value={vendorId} onChange={setVendorId} options={vendorOptions} placeholder="Choose a vendor" aria-label="Vendor" />
            </div>}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              value={lineDescription}
              onChange={(event) => setLineDescription(event.target.value)}
              label="When a line contains"
              placeholder="e.g. freight, office supplies"
              helperText="Use words that reliably describe the line."
              maxLength={500}
              required
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-content-secondary">Document type</label>
              <SearchableSelect value={documentType} onChange={setDocumentType} options={documentTypeOptions} placeholder="Any document type" clearable={false} aria-label="Document type" />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-content-secondary">Then use this {targetLabel.toLowerCase()}</label>
            <SearchableSelect
              value={targetRef}
              onChange={setTargetRef}
              options={targetOptions}
              placeholder={`Choose a ${targetLabel.toLowerCase()}`}
              emptyMessage={`No ${targetLabel.toLowerCase()}s are available. Sync your ERP and try again.`}
              aria-label={targetLabel}
            />
          </div>

          <div className="rounded-lg bg-surface-raised p-4 text-sm text-content-secondary">
            <p className="font-medium text-content-primary">Policy preview</p>
            <p className="mt-1">
              For {scope === 'vendor' ? 'the selected vendor' : 'all vendors'}, when {documentType || 'any document'} has a line containing “{lineDescription.trim() || '…'}”, suggest the selected {targetLabel.toLowerCase()}.
            </p>
          </div>
        </div>}

        <div className="mt-6 flex justify-end gap-3 border-t border-border-subtle pt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} loading={createPolicy.isPending} disabled={!isValid || isLoadingOptions}>
            <Plus className="mr-1.5 h-4 w-4" /> Submit for review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
